import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    IoCalendarOutline,
    IoClose,
    IoPeopleOutline,
    IoPieChartOutline,
    IoRadio,
    IoTimeOutline
} from 'react-icons/io5';
import HelpIcon from '../Documentation/HelpIcon';
import { getScheduleEntries } from '../../services/apiService';
import { convertToScheduleEntries } from '../../utils/scheduleUtils';
import {
    buildDraftVersionStats,
    collapseSlices,
    formatEntryCount,
    formatHours
} from '../../utils/draftVersionStats';
import {
    formatHistoryTimestamp,
    formatVersionLabel,
    getDraftTitle,
    isHistoryDraft
} from '../../utils/scheduleDraftKind';
import {
    formatPeriodBoundsLabel,
    formatPeriodShortMonthYearRange
} from '../../utils/yearStructurePeriodUtils';
import { daysOfWeek } from '../../config/constants';

const polar = (cx, cy, radius, angleDeg) => {
    const radians = ((angleDeg - 90) * Math.PI) / 180;
    return [cx + radius * Math.cos(radians), cy + radius * Math.sin(radians)];
};

const donutSegmentPath = (cx, cy, innerRadius, outerRadius, startAngle, endAngle) => {
    const sweep = Math.max(0, Math.min(359.999, endAngle - startAngle));
    if (sweep <= 0) return '';
    if (sweep >= 359.999) {
        return [
            `M ${cx} ${cy - outerRadius}`,
            `A ${outerRadius} ${outerRadius} 0 1 1 ${cx} ${cy + outerRadius}`,
            `A ${outerRadius} ${outerRadius} 0 1 1 ${cx} ${cy - outerRadius}`,
            `M ${cx} ${cy - innerRadius}`,
            `A ${innerRadius} ${innerRadius} 0 1 0 ${cx} ${cy + innerRadius}`,
            `A ${innerRadius} ${innerRadius} 0 1 0 ${cx} ${cy - innerRadius}`,
            'Z'
        ].join(' ');
    }

    const largeArc = sweep > 180 ? 1 : 0;
    const [outerStartX, outerStartY] = polar(cx, cy, outerRadius, startAngle);
    const [outerEndX, outerEndY] = polar(cx, cy, outerRadius, startAngle + sweep);
    const [innerEndX, innerEndY] = polar(cx, cy, innerRadius, startAngle + sweep);
    const [innerStartX, innerStartY] = polar(cx, cy, innerRadius, startAngle);

    return [
        `M ${outerStartX} ${outerStartY}`,
        `A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${outerEndX} ${outerEndY}`,
        `L ${innerEndX} ${innerEndY}`,
        `A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${innerStartX} ${innerStartY}`,
        'Z'
    ].join(' ');
};

function DonutChart({ slices, metric, activeKey, onActiveKeyChange, emptyLabel }) {
    const total = slices.reduce((sum, slice) => sum + (slice[metric] || 0), 0);
    let cursor = 0;
    const segments = slices.map((slice) => {
        const value = slice[metric] || 0;
        const startAngle = total > 0 ? (cursor / total) * 360 : 0;
        cursor += value;
        const endAngle = total > 0 ? (cursor / total) * 360 : 0;
        return { ...slice, value, startAngle, endAngle };
    });

    if (total <= 0) {
        return (
            <div className="flex h-[168px] flex-col items-center justify-center text-center">
                <div className="flex h-28 w-28 items-center justify-center rounded-full border-[14px] border-slate-100 bg-white">
                    <span className="text-[11px] font-medium text-slate-400">—</span>
                </div>
                <p className="mt-2 text-[11px] text-slate-400">{emptyLabel}</p>
            </div>
        );
    }

    return (
        <svg viewBox="0 0 160 160" className="mx-auto h-[168px] w-[168px]" role="img">
            {segments.map((segment) => {
                const isActive = !activeKey || activeKey === segment.key;
                return (
                    <path
                        key={segment.key}
                        d={donutSegmentPath(80, 80, 46, 72, segment.startAngle, segment.endAngle)}
                        fill={segment.color}
                        className="cursor-pointer transition-opacity duration-200"
                        style={{ opacity: isActive ? 1 : 0.28 }}
                        onMouseEnter={() => onActiveKeyChange(segment.key)}
                        onMouseLeave={() => onActiveKeyChange(null)}
                    >
                        <title>{`${segment.label}: ${metric === 'hours' ? formatHours(segment.value) : formatEntryCount(segment.value)}`}</title>
                    </path>
                );
            })}
            <circle cx="80" cy="80" r="38" fill="white" />
            <text x="80" y="76" textAnchor="middle" className="fill-slate-900" style={{ fontSize: '20px', fontWeight: 700 }}>
                {metric === 'hours' ? formatHours(total).replace(' h', '') : total}
            </text>
            <text x="80" y="94" textAnchor="middle" className="fill-slate-400" style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '0.08em' }}>
                {metric === 'hours' ? 'HOURS' : 'ENTRIES'}
            </text>
        </svg>
    );
}

function BreakdownCard({ title, slices, metric, emptyLabel }) {
    const [activeKey, setActiveKey] = useState(null);
    const visible = useMemo(() => collapseSlices(slices, metric, 6), [slices, metric]);
    const total = visible.reduce((sum, slice) => sum + (slice[metric] || 0), 0);

    return (
        <section className="flex min-h-[280px] flex-col rounded-2xl border border-slate-200/80 bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
            <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                {title}
            </h4>
            <DonutChart
                slices={visible}
                metric={metric}
                activeKey={activeKey}
                onActiveKeyChange={setActiveKey}
                emptyLabel={emptyLabel}
            />
            <ul className="mt-1 space-y-1.5">
                {visible.map((slice) => {
                    const value = slice[metric] || 0;
                    const percent = total > 0 ? Math.round((value / total) * 100) : 0;
                    const isActive = activeKey === slice.key;
                    return (
                        <li key={slice.key}>
                            <button
                                type="button"
                                onMouseEnter={() => setActiveKey(slice.key)}
                                onMouseLeave={() => setActiveKey(null)}
                                className={`flex w-full items-center gap-2 rounded-lg px-1.5 py-1 text-left transition-colors ${isActive ? 'bg-slate-50' : ''}`}
                            >
                                <span
                                    className="h-2.5 w-2.5 flex-none rounded-full"
                                    style={{ backgroundColor: slice.color }}
                                />
                                <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-slate-700">
                                    {slice.label}
                                </span>
                                <span className="flex-none text-[11px] tabular-nums text-slate-400">
                                    {percent}%
                                </span>
                                <span className="w-12 flex-none text-right text-[12px] font-semibold tabular-nums text-slate-800">
                                    {metric === 'hours' ? formatHours(value) : value}
                                </span>
                            </button>
                        </li>
                    );
                })}
            </ul>
        </section>
    );
}

function StatTile({ label, value, hint, accent = false }) {
    return (
        <div className={`rounded-2xl border px-4 py-3 ${accent
            ? 'border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-white'
            : 'border-slate-200/80 bg-white'
        }`}>
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                {label}
            </p>
            <p className={`mt-1 text-2xl font-semibold tracking-tight ${accent ? 'text-indigo-950' : 'text-slate-900'}`}>
                {value}
            </p>
            {hint ? <p className="mt-0.5 text-[11px] text-slate-500">{hint}</p> : null}
        </div>
    );
}

function WeekCoverageChart({ coverage, metric, periodColor }) {
    const [activeKey, setActiveKey] = useState(null);
    const weeks = coverage?.weeks || [];
    const max = Math.max(1, ...weeks.map((week) => (metric === 'hours' ? week.hours : week.count)));
    const active = weeks.find((week) => `${week.year || ''}-${week.week}` === activeKey) || null;

    const monthBands = useMemo(() => {
        const bands = [];
        weeks.forEach((week, index) => {
            const label = week.monthLabel;
            if (!label) return;
            const last = bands[bands.length - 1];
            if (last && last.label === label) {
                last.span += 1;
                return;
            }
            bands.push({ label, span: 1, start: index });
        });
        return bands;
    }, [weeks]);

    const labelEvery = weeks.length <= 12 ? 1 : weeks.length <= 24 ? 2 : 4;

    if (!weeks.length) return null;

    return (
        <section className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
            <div className="flex flex-wrap items-end justify-between gap-2">
                <div>
                    <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                        Week coverage
                    </h4>
                    <p className="mt-1 text-sm font-medium text-slate-800">
                        {coverage.coveredCount} of {coverage.totalWeeks} week{coverage.totalWeeks === 1 ? '' : 's'} have entries
                    </p>
                </div>
                <p className="min-h-[1.25rem] text-[12px] text-slate-500">
                    {active
                        ? `W${active.week}${active.year ? ` ${active.year}` : ''}${active.dateLabel ? ` · ${active.dateLabel}` : ''} · ${metric === 'hours' ? formatHours(active.hours) : formatEntryCount(active.count)}`
                        : coverage.emptyCount
                            ? `${coverage.emptyCount} empty week${coverage.emptyCount === 1 ? '' : 's'}`
                            : 'Every week has at least one entry'}
                </p>
            </div>

            {monthBands.length > 0 ? (
                <div className="mt-4 flex text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                    {monthBands.map((band) => (
                        <span
                            key={`${band.label}-${band.start}`}
                            className="truncate border-l border-slate-100 first:border-l-0 px-0.5"
                            style={{ flex: band.span }}
                        >
                            {band.span >= 2 ? band.label : ''}
                        </span>
                    ))}
                </div>
            ) : null}

            <div className="mt-2 flex h-28 items-end gap-px">
                {weeks.map((week) => {
                    const value = metric === 'hours' ? week.hours : week.count;
                    const key = `${week.year || ''}-${week.week}`;
                    const height = value > 0 ? Math.max(8, Math.round((value / max) * 104)) : 3;
                    const isActive = activeKey === key;
                    return (
                        <button
                            key={key}
                            type="button"
                            onMouseEnter={() => setActiveKey(key)}
                            onMouseLeave={() => setActiveKey(null)}
                            onFocus={() => setActiveKey(key)}
                            onBlur={() => setActiveKey(null)}
                            className="group relative flex min-w-0 flex-1 flex-col items-center justify-end"
                            title={`W${week.week}: ${formatEntryCount(week.count)} · ${formatHours(week.hours)}`}
                            aria-label={`Week ${week.week}${week.year ? ` ${week.year}` : ''}: ${formatEntryCount(week.count)}`}
                        >
                            <span
                                className={`w-full max-w-[18px] rounded-t-md transition-all duration-150 ${week.isCurrent ? 'ring-2 ring-offset-1 ring-slate-900' : ''}`}
                                style={{
                                    height,
                                    background: value > 0
                                        ? (isActive
                                            ? periodColor
                                            : `linear-gradient(180deg, ${periodColor}cc 0%, ${periodColor} 100%)`)
                                        : '#E2E8F0',
                                    opacity: !activeKey || isActive ? 1 : 0.35
                                }}
                            />
                        </button>
                    );
                })}
            </div>

            <div className="mt-1.5 flex">
                {weeks.map((week, index) => {
                    const show = index === 0 || index === weeks.length - 1 || index % labelEvery === 0;
                    return (
                        <span
                            key={`${week.year || ''}-${week.week}-label`}
                            className="min-w-0 flex-1 text-center text-[10px] tabular-nums text-slate-400"
                        >
                            {show ? week.week : ''}
                        </span>
                    );
                })}
            </div>
        </section>
    );
}

function CompletenessRow({ label, count, total }) {
    const percent = total > 0 ? Math.round((count / total) * 100) : 0;
    return (
        <div>
            <div className="mb-1 flex items-center justify-between text-[12px]">
                <span className="text-slate-600">{label}</span>
                <span className="tabular-nums text-slate-500">{count}/{total}</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
                <div
                    className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-cyan-400 transition-all duration-500"
                    style={{ width: `${percent}%` }}
                />
            </div>
        </div>
    );
}

const DAY_SHORT = {
    monday: 'Mon',
    tuesday: 'Tue',
    wednesday: 'Wed',
    thursday: 'Thu',
    friday: 'Fri',
    saturday: 'Sat',
    sunday: 'Sun'
};

function formatWhen(value) {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
}

const DraftVersionInfoModal = ({
    isOpen,
    onClose,
    draft = null,
    period = null,
    isLive = false
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [stats, setStats] = useState(null);
    const [metric, setMetric] = useState('count');

    const draftName = draft?.draft_name || '';

    useEffect(() => {
        if (!isOpen) return undefined;

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    useEffect(() => {
        if (!isOpen) return undefined;

        let cancelled = false;
        setMetric('count');
        setStats(null);
        setError('');

        const load = async () => {
            if (!draftName) {
                setError('This version cannot be inspected.');
                return;
            }

            setLoading(true);
            try {
                const rawEntries = await getScheduleEntries({ draft_name: draftName });
                if (cancelled) return;
                const entries = convertToScheduleEntries(Array.isArray(rawEntries) ? rawEntries : []);
                setStats(buildDraftVersionStats(entries, { period }));
            } catch (err) {
                if (cancelled) return;
                setError(err?.message || 'Failed to load version details.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [isOpen, draftName, period]);

    const title = getDraftTitle(draft);
    const versionLabel = formatVersionLabel(draft?.version_number);
    const historyLabel = isHistoryDraft(draft)
        ? formatHistoryTimestamp(draft.created_at || draft.last_modified)
        : '';
    const periodColor = period?.color || '#4F46E5';
    const periodTimeframe = period ? formatPeriodShortMonthYearRange(period) : '';
    const periodBounds = period ? formatPeriodBoundsLabel(period) : '';
    const lastModified = formatWhen(draft?.last_modified);
    const createdAt = formatWhen(draft?.created_at);

    const dayMax = useMemo(() => {
        if (!stats?.days?.length) return 1;
        return Math.max(...stats.days.map((day) => day.count), 1);
    }, [stats]);

    if (!isOpen) return null;

    return createPortal(
        <div
            className="fixed inset-0 z-[1200] flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-sm"
            onClick={onClose}
            role="presentation"
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="draft-version-info-title"
                className="relative flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-3xl bg-slate-50 shadow-2xl shadow-slate-950/20"
                onClick={(event) => event.stopPropagation()}
            >
                <div
                    className="relative overflow-hidden bg-slate-950 px-6 pb-6 pt-5 text-white"
                    style={{
                        backgroundImage: `radial-gradient(circle at 92% -10%, ${periodColor}55 0%, transparent 42%), linear-gradient(160deg, #0f172a 0%, #111827 60%, #0b1220 100%)`
                    }}
                >
                    <span
                        className="absolute inset-x-0 top-0 h-1"
                        style={{ backgroundColor: periodColor }}
                        aria-hidden="true"
                    />
                    <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                            <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                                <IoPieChartOutline className="h-3.5 w-3.5" />
                                Version overview
                            </p>
                            <h2 id="draft-version-info-title" className="mt-1.5 truncate text-2xl font-semibold tracking-tight">
                                {historyLabel || title}
                            </h2>
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                                {versionLabel ? (
                                    <span className="rounded-full bg-white/10 px-2.5 py-0.5 text-[11px] font-semibold text-slate-100">
                                        {versionLabel}
                                    </span>
                                ) : null}
                                {isLive ? (
                                    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-400/15 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-200">
                                        <span className="relative flex h-1.5 w-1.5">
                                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-300 opacity-70" />
                                            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-300" />
                                        </span>
                                        Live
                                    </span>
                                ) : (
                                    <span className="rounded-full bg-white/10 px-2.5 py-0.5 text-[11px] font-medium text-slate-300">
                                        {isHistoryDraft(draft) ? 'Restore point' : 'Working version'}
                                    </span>
                                )}
                                {period?.name ? (
                                    <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-0.5 text-[11px] text-slate-300">
                                        <span
                                            className="h-1.5 w-1.5 rounded-full"
                                            style={{ backgroundColor: periodColor }}
                                        />
                                        {period.name}
                                    </span>
                                ) : null}
                            </div>
                            {(periodTimeframe || lastModified) ? (
                                <p className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-400">
                                    {periodTimeframe ? (
                                        <span className="inline-flex items-center gap-1.5">
                                            <IoCalendarOutline className="h-3.5 w-3.5" />
                                            {periodTimeframe}
                                            {periodBounds ? <span className="text-slate-500">· {periodBounds}</span> : null}
                                        </span>
                                    ) : null}
                                    {lastModified ? (
                                        <span className="inline-flex items-center gap-1.5">
                                            <IoTimeOutline className="h-3.5 w-3.5" />
                                            Updated {lastModified}
                                        </span>
                                    ) : null}
                                </p>
                            ) : null}
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-xl p-2 text-slate-400 transition-colors hover:bg-white/10 hover:text-white"
                            aria-label="Close version info"
                        >
                            <IoClose className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
                    {loading ? (
                        <div className="grid gap-4 sm:grid-cols-4">
                            {Array.from({ length: 4 }).map((_, index) => (
                                <div key={index} className="h-24 animate-pulse rounded-2xl bg-slate-200/70" />
                            ))}
                            <div className="col-span-full mt-2 grid gap-4 sm:grid-cols-3">
                                {Array.from({ length: 3 }).map((_, index) => (
                                    <div key={index} className="h-72 animate-pulse rounded-2xl bg-slate-200/60" />
                                ))}
                            </div>
                        </div>
                    ) : error ? (
                        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-6 text-sm text-rose-800">
                            {error}
                        </div>
                    ) : stats ? (
                        stats.entryCount === 0 ? (
                            <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-16 text-center">
                                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                                    <IoPieChartOutline className="h-7 w-7" />
                                </div>
                                <p className="mt-4 text-base font-semibold text-slate-800">No entries yet</p>
                                <p className="mt-1 max-w-sm text-sm text-slate-500">
                                    This version is empty. Add classes or events and the mix of subjects, rooms, and teachers will appear here.
                                </p>
                            </div>
                        ) : (
                        <div className="space-y-5">
                            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                                <StatTile
                                    accent
                                    label="Entries"
                                    value={stats.entryCount.toLocaleString()}
                                    hint={formatEntryCount(stats.entryCount)}
                                />
                                <StatTile
                                    label="Scheduled hours"
                                    value={formatHours(stats.totalHours).replace(' h', '')}
                                    hint="Duration × weeks"
                                />
                                <StatTile
                                    label="People"
                                    value={stats.uniqueTeachers}
                                    hint={`${stats.uniqueGroups} group${stats.uniqueGroups === 1 ? '' : 's'}`}
                                />
                                <StatTile
                                    label="Places"
                                    value={stats.uniqueRooms}
                                    hint={`${stats.uniqueSubjects} subject${stats.uniqueSubjects === 1 ? '' : 's'}`}
                                />
                            </div>

                            <div className="flex items-center justify-between gap-3">
                                <p className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
                                    Mix across this version
                                    <HelpIcon
                                        documentationId="version-info-overview"
                                        position="relative"
                                        size={14}
                                    />
                                </p>
                                <div className="inline-flex rounded-full border border-slate-200 bg-white p-0.5 text-[12px] font-medium">
                                    <button
                                        type="button"
                                        onClick={() => setMetric('count')}
                                        className={`rounded-full px-3 py-1 transition-colors ${metric === 'count' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-800'}`}
                                    >
                                        Entries
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setMetric('hours')}
                                        className={`rounded-full px-3 py-1 transition-colors ${metric === 'hours' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-800'}`}
                                    >
                                        Hours
                                    </button>
                                </div>
                            </div>

                            <div className="grid gap-3 lg:grid-cols-3">
                                <BreakdownCard
                                    title="Subjects"
                                    slices={stats.subjects}
                                    metric={metric}
                                    emptyLabel="No subjects yet"
                                />
                                <BreakdownCard
                                    title="Rooms"
                                    slices={stats.rooms}
                                    metric={metric}
                                    emptyLabel="No rooms yet"
                                />
                                <BreakdownCard
                                    title="Teachers"
                                    slices={stats.teachers}
                                    metric={metric}
                                    emptyLabel="No teachers yet"
                                />
                            </div>

                            <WeekCoverageChart
                                coverage={stats.weekCoverage}
                                metric={metric}
                                periodColor={periodColor}
                            />

                            <div className="grid gap-3 lg:grid-cols-2">
                                <section className="rounded-2xl border border-slate-200/80 bg-white p-4">
                                    <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                                        Completeness
                                    </h4>
                                    <div className="mt-3 space-y-3">
                                        <CompletenessRow label="Has a room" count={stats.completeness.withRoom} total={stats.entryCount} />
                                        <CompletenessRow label="Has a teacher" count={stats.completeness.withTeacher} total={stats.entryCount} />
                                        <CompletenessRow label="Has groups" count={stats.completeness.withGroups} total={stats.entryCount} />
                                        <CompletenessRow label="Has a time" count={stats.completeness.withTime} total={stats.entryCount} />
                                    </div>
                                </section>

                                <section className="rounded-2xl border border-slate-200/80 bg-white p-4">
                                    <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                                        Days & types
                                    </h4>
                                    <div className="mt-4 flex items-end gap-2">
                                        {daysOfWeek.map((day) => {
                                            const match = stats.days.find((item) => item.day === day);
                                            const count = match?.count || 0;
                                            const height = Math.max(count > 0 ? (count / dayMax) * 72 : 4, 4);
                                            return (
                                                <div key={day} className="flex min-w-0 flex-1 flex-col items-center gap-1.5">
                                                    <div className="flex h-[72px] w-full items-end justify-center">
                                                        <div
                                                            className="w-full max-w-[18px] rounded-full bg-gradient-to-t from-indigo-500 to-cyan-300"
                                                            style={{ height }}
                                                            title={`${DAY_SHORT[day]}: ${formatEntryCount(count)}`}
                                                        />
                                                    </div>
                                                    <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
                                                        {DAY_SHORT[day]}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div className="mt-4 flex flex-wrap gap-2 text-[12px]">
                                        <span className="rounded-full bg-slate-100 px-2.5 py-1 font-medium text-slate-700">
                                            {stats.types.class} class{stats.types.class === 1 ? '' : 'es'}
                                        </span>
                                        <span className="rounded-full bg-slate-100 px-2.5 py-1 font-medium text-slate-700">
                                            {stats.types.event} event{stats.types.event === 1 ? '' : 's'}
                                        </span>
                                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 font-medium text-slate-700">
                                            <IoPeopleOutline className="h-3.5 w-3.5" />
                                            {stats.uniqueGroups} group{stats.uniqueGroups === 1 ? '' : 's'}
                                        </span>
                                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 font-medium text-slate-700">
                                            <IoRadio className="h-3.5 w-3.5" />
                                            {stats.uniqueWeeks} week{stats.uniqueWeeks === 1 ? '' : 's'} covered
                                        </span>
                                    </div>
                                </section>
                            </div>

                            {createdAt ? (
                                <p className="text-[11px] text-slate-400">
                                    Created {createdAt}
                                </p>
                            ) : null}
                        </div>
                        )
                    ) : null}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default DraftVersionInfoModal;
